import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Button, ComponentSync, LL_H_C, LL_V_L, ModuleFE_Dialog } from '@nu-art/thunderstorm-frontend/index';
import { ModuleFE_VolatileImport } from './ModuleFE_VolatileImport.js';
import { ModuleFE_PathwayImport } from './ModuleFE_PathwayImport.js';
import { ModuleFE_DiseaseProfileImport } from './ModuleFE_DiseaseProfileImport.js';
import { WorkHubItem_Pathway } from '@app/pathway-frontend/search/items/pathway';
import { WorkHubItem_DiseaseProfile } from '@app/dp-frontend/search/items/disease-profile';
import { DBDef_Pathway } from '@app/pathway-shared';
import { DBDef_DiseaseProfile } from '@app/dp-shared';
import { ModuleFE_Pathway } from '@app/pathway-frontend';
import { ModuleFE_DiseaseProfile, ModuleFE_DiseaseView } from '@app/dp-frontend';
import { Manager_DiseaseProfile_MultiVarTab_Values, Manager_DiseaseProfile_ProfileTab_General } from '@app/dp-frontend/ui/_managers/Manager_DiseaseProfile/types';
import { md5 } from '@nu-art/ts-common';
import './Dialog_VolatileImport.scss';
const entityTypeOptions = [
    { value: 'pathway', label: 'Pathway', dbKey: DBDef_Pathway.dbKey },
    { value: 'disease-profile', label: 'Disease Profile', dbKey: DBDef_DiseaseProfile.dbKey },
];
export class Dialog_VolatileImport extends ComponentSync {
    fileInputRef = React.createRef();
    htmlInputRef = React.createRef();
    static show = () => {
        ModuleFE_Dialog.show({ content: _jsx(Dialog_VolatileImport, {}), closeOverlayOnClick: () => false });
    };
    deriveStateFromProps(nextProps, state) {
        state.importing ??= false;
        return state;
    }
    collectPathwayValidationErrors = async (file) => {
        try {
            return (await ModuleFE_PathwayImport.validatePathway(file)).errors;
        }
        catch (e) {
            return [{ code: 'INTERNAL_ERROR', message: e instanceof Error ? e.message : 'Validation request failed' }];
        }
    };
    collectDiseaseProfileValidationErrors = async (file) => {
        try {
            return (await ModuleFE_DiseaseProfileImport.validateDiseaseProfile(file)).errors;
        }
        catch (e) {
            return [{ code: 'INTERNAL_ERROR', message: e instanceof Error ? e.message : 'Validation request failed' }];
        }
    };
    onImport = async () => {
        const file = this.fileInputRef.current?.files?.[0];
        if (!file || !this.state.selectedType)
            return;
        const option = entityTypeOptions.find(o => o.value === this.state.selectedType);
        this.setState({ importing: true, error: undefined });
        try {
            const entry = await ModuleFE_VolatileImport.importFromCSV(file, option.dbKey);
            const primaryId = entry.primaryEntity._id;
            if (option.value === 'pathway') {
                // Dry-run the same File the backend will re-parse on Save; preview opens regardless of errors.
                const errors = await this.collectPathwayValidationErrors(file);
                // Optional HTML the user attached; stashed alongside the CSV and uploaded on Save to KM.
                const htmlFile = this.htmlInputRef.current?.files?.[0];
                ModuleFE_VolatileImport.stashImportPayload(entry.importId, file, errors, htmlFile);
                const pathway = ModuleFE_Pathway.cache.unique(primaryId);
                const tabId = md5(`volatile-${DBDef_Pathway.dbKey}-${primaryId}`);
                // Register BEFORE openTab: openTab synchronously dispatches OnWorkHubTabsUpdated and mounts
                // Manager_Pathway, whose Save/errors slots resolve the active import by tab id. Registering
                // after would leave the map empty at first paint and the Save/errors UI would never show.
                ModuleFE_VolatileImport.registerVolatileTab(tabId, entry.importId);
                WorkHubItem_Pathway.openTab(tabId, `[Preview] ${pathway?.label ?? 'Imported Pathway'}`, {
                    itemId: primaryId,
                    editMode: false,
                });
            }
            else {
                // Dry-run the same File the backend will re-parse on Save; preview opens regardless of errors.
                const errors = await this.collectDiseaseProfileValidationErrors(file);
                // Optional HTML the user attached; stashed alongside the CSV and uploaded on Save to KM.
                const htmlFile = this.htmlInputRef.current?.files?.[0];
                ModuleFE_VolatileImport.stashDiseaseProfileImportPayload(entry.importId, file, errors, htmlFile);
                const dp = ModuleFE_DiseaseProfile.cache.unique(primaryId);
                const diseaseView = ModuleFE_DiseaseView.cache.all()[0];
                const tabId = md5(`volatile-${DBDef_DiseaseProfile.dbKey}-${primaryId}`);
                ModuleFE_VolatileImport.registerVolatileTab(tabId, entry.importId);
                WorkHubItem_DiseaseProfile.openTab(tabId, `[Preview] ${dp?.label ?? 'Imported Disease Profile'}`, {
                    itemId: primaryId,
                    editMode: false,
                    profile: {
                        selectedTab: Manager_DiseaseProfile_ProfileTab_General,
                        selectedDiseaseViewId: diseaseView?._id,
                    },
                    multiVar: {
                        selectedTab: Manager_DiseaseProfile_MultiVarTab_Values,
                    },
                });
            }
            ModuleFE_Dialog.close();
        }
        catch (e) {
            this.setState({ importing: false, error: e instanceof Error ? e.message : 'Import failed' });
        }
    };
    // Optional HTML picker: shown for pathway and disease-profile once a CSV is chosen (the CSV is the
    // primary artifact; the HTML just accompanies it). The file lives in the input ref until Save, like the CSV.
    renderHtmlPicker = () => {
        return _jsxs(_Fragment, { children: [_jsx("label", { className: 'dialog-volatile-import__label', children: "HTML (optional)" }), _jsxs(LL_H_C, { className: 'dialog-volatile-import__file-picker', onClick: e => e.stopPropagation(), onMouseDown: e => e.stopPropagation(), children: [_jsx("input", { ref: this.htmlInputRef, id: 'volatile-import-html', type: 'file', accept: '.html,.htm', style: { display: 'none' }, onChange: () => this.forceUpdate() }), _jsx("label", { htmlFor: 'volatile-import-html', className: 'dialog-volatile-import__type-btn', children: "Choose File" }), _jsx("span", { className: 'dialog-volatile-import__file-name', children: this.htmlInputRef.current?.files?.[0]?.name ?? 'No file chosen' })] })] });
    };
    render() {
        return _jsxs(LL_V_L, { className: 'dialog-volatile-import', children: [_jsx(LL_H_C, { className: 'dialog-volatile-import__header', children: _jsx("span", { children: "Import CSV Preview" }) }), _jsxs(LL_V_L, { className: 'dialog-volatile-import__body', children: [_jsx("label", { className: 'dialog-volatile-import__label', children: "Entity Type" }), _jsx(LL_H_C, { className: 'dialog-volatile-import__type-selector', children: entityTypeOptions.map(opt => (_jsx("button", { className: `dialog-volatile-import__type-btn${this.state.selectedType === opt.value ? ' selected' : ''}`, onClick: () => this.setState({ selectedType: opt.value }), children: opt.label }, opt.value))) }), _jsx("label", { className: 'dialog-volatile-import__label', children: "CSV File" }), _jsxs(LL_H_C, { className: 'dialog-volatile-import__file-picker', onClick: e => e.stopPropagation(), onMouseDown: e => e.stopPropagation(), children: [_jsx("input", { ref: this.fileInputRef, id: 'volatile-import-file', type: 'file', accept: '.csv', style: { display: 'none' }, onChange: () => this.forceUpdate() }), _jsx("label", { htmlFor: 'volatile-import-file', className: 'dialog-volatile-import__type-btn', children: "Choose File" }), _jsx("span", { className: 'dialog-volatile-import__file-name', children: this.fileInputRef.current?.files?.[0]?.name ?? 'No file chosen' })] }), !!this.state.selectedType && !!this.fileInputRef.current?.files?.[0] && this.renderHtmlPicker(), this.state.error && _jsx("div", { className: 'dialog-volatile-import__error', children: this.state.error })] }), _jsxs(LL_H_C, { className: 'dialog-volatile-import__actions', children: [_jsx(Button, { variant: 'secondary', onClick: () => ModuleFE_Dialog.close(), children: "Cancel" }), _jsx(Button, { variant: 'primary', disabled: !this.state.selectedType || !this.fileInputRef.current?.files?.[0] || this.state.importing, onClick: this.onImport, children: this.state.importing ? 'Importing...' : 'Import & Preview' })] })] });
    }
}
//# sourceMappingURL=Dialog_VolatileImport.js.map